import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { formatDateAsString } from '../../../../util/StringUtil';
import { CREATE_CAPA_REVIEW_ROLES, EDIT_CAPA_REVIEW_ROLES, hasRoles, mergeRoles } from '../../../../util/RoleUtil';
import { DATE_PICKER_POPPER_MODIFIERS } from '../../../../util/DateUtil';

import { retrieveStudyDropdownsSpecific } from '../../../../services/StudyService';
import { retrieveAllSystemUserPersons, viewStaffForStudy } from '../../../../services/PersonService';
import { createCAPA } from '../../../../services/CAPAService';
import { viewNotificationInterval } from '../../../../services/NotificationIntervalService';

import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { SaveButton } from '../../../common/button/save-button/SaveButton';

import './CAPAReviewCreate.css';

const roles = mergeRoles(CREATE_CAPA_REVIEW_ROLES, EDIT_CAPA_REVIEW_ROLES);

export const CAPAReviewGeneralTab = ({
  capa,
  context,
  navigateTo,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
  handleUpdate,
}) => {
  const [dateInitiated, setDateInitiated] = useState(new Date());
  const [targetDate, setTargetDate] = useState(null);
  const [capaCreator, setCapaCreator] = useState(null);
  const [capaTrigger, setCapaTrigger] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(null);
  const [status, setStatus] = useState(null);
  const [specifyOtherSeverity, setSpecifyOtherSeverity] = useState('');
  const [specifyOtherCategory, setSpecifyOtherCategory] = useState('');
  const [emailFrequencyHours, setEmailFrequencyHours] = useState(0);
  const [escalateAfterDays, setEscalateAfterDays] = useState(0);
  const [showOtherSeverity, setShowOtherSeverity] = useState(false);
  const [showOtherCategory, setShowOtherCategory] = useState(false);
  const [showStudies, setShowStudies] = useState(false);
  const [responsiblePersonList, setResponsiblePersonList] = useState([]);
  const [selectedResponsiblePerson, setSelectedResponsiblePerson] = useState(null);
  const [selectedCcUsers, setSelectedCcUsers] = useState([]);
  const [selectedEscalationUsers, setSelectedEscalationUsers] = useState([]);
  const [selectedStudies, setSelectedStudies] = useState([]);
  const [studiesList, setStudiesList] = useState(null);
  const [dateInitiatedError, setDateInitiatedError] = useState('');
  const [capaCreatorError, setCapaCreatorError] = useState('');
  const [capaTriggerError, setCapaTriggerError] = useState('');
  const [severityError, setSeverityError] = useState('');
  const [specifySeverityError, setSpecifySeverityError] = useState('');
  const [responsiblePersonError, setResponsiblePersonError] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [categoryStudyError, setCategoryStudyError] = useState('');
  const [categoryOtherError, setCategoryOtherError] = useState('');
  const [targetDateError, setTargetDateError] = useState('');
  const [statusError, setStatusError] = useState('');
  const [escalatedUsersError, setEscalatedUsersError] = useState('');

  const severityList = useRef(['Minor', 'Major', 'Critical', 'Other']);
  const statusList = useRef(['Ongoing', 'In Review', 'Approved']);
  const triggersList = useRef([
    'Site Initiation',
    'Monitoring Visit',
    'Complaints',
    'Trends',
    'Audit/Inspection',
    'Risk Identification',
    'Internal Review',
    'Sponsor Visit',
  ]);
  const categoryList = useRef([
    'Study',
    'General',
    'Safety',
    'Informed Consent',
    'Eligibility',
    'Protocol Implementation',
    'Data Integrity',
    'SOP/WPG/MOP implementation',
    'Equipment',
    'Other',
  ]);

  const handleResponsiblePersonsResponse = (data, error) => {
    if (!data) {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });

      return;
    }

    if (data.responseStatus !== 'Success') {
      showToaster(data);
      return;
    }

    const formattedUsers = data.viewAllPersonSystemUserVOs.map((user) => ({
      value: user,
      label: user.name,
    }));

    setResponsiblePersonList(formattedUsers);
  };

  const retrieveDropdowns = useCallback(async () => {
    setSpinnerText('Retrieving Users');
    const [data, error] = await retrieveAllSystemUserPersons(context.username);

    handleResponsiblePersonsResponse(data, error);

    setSpinnerText(null);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context.username]);

  const retrieveStaffForStudy = async (study) => {
    setSpinnerText('Retrieving Users');

    const payload = {
      studyExternalReference: study.studyExternalReference,
      locationExternalReference: study.locationExternalReference,
    };
    const [data, error] = await viewStaffForStudy(context.username, payload);

    handleResponsiblePersonsResponse(data, error);

    setSpinnerText(null);
  };

  useEffect(() => {
    if (selectedStudies.length === 0) {
      return;
    }

    if (selectedStudies.length > 1) {
      retrieveDropdowns();
      return;
    }

    retrieveStaffForStudy(selectedStudies[0].value);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudies]);

  const getNotificationInterval = useCallback(async () => {
    setSpinnerText('Retrieving Notification Settings');
    const [data, error] = await viewNotificationInterval(context.username);
    if (data) {
      if (data.responseStatus === 'Success' && data.notificationInterval) {
        setEscalateAfterDays(data.notificationInterval.capaEscalationDays || '');
        setEmailFrequencyHours(data.notificationInterval.capaHours || '');
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context, setSpinnerText, showToaster]);

  useEffect(() => {
    if (context?.loggedInUser && !editMode && responsiblePersonList) {
      const foundPerson = responsiblePersonList.find(
        (person) => person.value.systemUserExternalReference === context.loggedInUser.externalReference
      );
      setCapaCreator(foundPerson);
    }
  }, [context.loggedInUser, responsiblePersonList, editMode]);

  useEffect(() => {
    retrieveDropdowns();

    if (!capa || Object.keys(capa).length === 0) {
      return;
    }

    setDateInitiated(capa.dateInitiated ? new Date(capa.dateInitiated) : null);
    setTargetDate(capa.targetDate ? new Date(capa.targetDate) : null);
    setCapaCreator(
      capa.capaCreator
        ? typeof capa.capaCreator === 'object'
          ? { value: capa.capaCreatorExternalReference.value, label: capa.capaCreator.label }
          : { value: capa.capaCreatorExternalReference, label: capa.capaCreator }
        : null
    );

    setCapaTrigger(
      capa.capaTrigger
        ? typeof capa.capaTrigger === 'object'
          ? { value: capa.capaTrigger.value, label: capa.capaTrigger.label }
          : { value: capa.capaTrigger, label: capa.capaTrigger }
        : null
    );

    setSeverity(
      capa.severity
        ? typeof capa.severity === 'object'
          ? { value: capa.severity.value, label: capa.severity.label }
          : { value: capa.severity, label: capa.severity }
        : null
    );

    if (capa.severity === 'Other') {
      setShowOtherSeverity(true);
    }

    setCategory(
      capa.capaCategory
        ? typeof capa.capaCategory === 'object'
          ? { value: capa.capaCategory.value, label: capa.capaCategory.label }
          : { value: capa.capaCategory, label: capa.capaCategory }
        : null
    );

    if (capa.capaCategory === 'Other') {
      setShowOtherCategory(true);
      setShowStudies(false);
    }

    if (capa.capaCategory === 'Study') {
      setShowStudies(true);
      setShowOtherCategory(false);

      const formattedStudies = capa.studies.map((study) => ({
        value: study,
        label: study?.name,
      }));

      setSelectedStudies(formattedStudies);

      if (formattedStudies.length === 1) {
        retrieveStaffForStudy(formattedStudies[0].value);
      }
    }

    setStatus(
      capa.status
        ? typeof capa.status === 'object'
          ? { value: capa.status.value, label: capa.status.label }
          : { value: capa.status, label: capa.status }
        : null
    );

    setDescription(capa.description || '');
    setSpecifyOtherSeverity(capa.specifySeverity || '');
    setSpecifyOtherCategory(capa.specifyCategory || '');

    setSelectedResponsiblePerson(
      capa.responsiblePerson
        ? typeof capa.responsiblePerson === 'object'
          ? { value: capa.responsiblePersonExternalReference.value, label: capa.responsiblePerson.label }
          : { value: capa.responsiblePersonExternalReference, label: capa.responsiblePerson }
        : null
    );

    const formattedCcUsers = capa.ccUsers.map((user) => ({
      value: user,
      label: user?.name,
    }));
    setSelectedCcUsers(formattedCcUsers);

    const formattedEscalatedUsers = capa.escalateToUsers.map((user) => ({
      value: user,
      label: user?.name,
    }));
    setSelectedEscalationUsers(formattedEscalatedUsers);

    setEmailFrequencyHours(capa.emailFrequencyHours || 0);
    setEscalateAfterDays(capa.escalateAfterDays || 0);

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capa, retrieveDropdowns]);

  useEffect(() => {
    if (!capa || (!capa.emailFrequencyHours && !capa.escalateAfterDays)) {
      getNotificationInterval();
    }
  }, [capa, getNotificationInterval]);

  const retrieveStudies = async () => {
    setSpinnerText('Retrieving Studies');
    const [studyData, studyError] = await retrieveStudyDropdownsSpecific(context.username, true);
    if (studyData) {
      if (studyData.responseStatus === 'Success') {
        const formattedStudies = studyData.dropdownStudyVOs.map((study) => ({
          value: study,
          label: `${study.name}`,
        }));
        setStudiesList(formattedStudies);
      } else {
        showToaster(studyData);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: studyError.message,
      });
    }
    setSpinnerText(null);
  };

  const handleSeverityChange = (selectedSeverity) => {
    setSeverity(selectedSeverity);

    if (selectedSeverity.label === 'Other') {
      setShowOtherSeverity(true);
    } else {
      setShowOtherSeverity(false);
    }
  };

  const handleCategoryChange = (selectedCategory) => {
    setCategory(selectedCategory);

    if (selectedCategory.label === 'Other') {
      setShowOtherCategory(true);
      setShowStudies(false);
    } else if (selectedCategory.label === 'Study') {
      retrieveStudies().then(() => setShowStudies(true));
      setShowOtherCategory(false);
    } else {
      setShowOtherCategory(false);
      setShowStudies(false);
    }
  };

  const validateFields = () => {
    let isValid = true;
    const currentDate = new Date();

    if (!dateInitiated) {
      setDateInitiatedError('*REQUIRED');
      isValid = false;
    } else if (dateInitiated > currentDate) {
      setDateInitiatedError('Date Initiated cannot be future dated.');
      isValid = false;
    } else {
      setDateInitiatedError('');
    }

    if (!capaCreator || capaCreator?.label === null) {
      setCapaCreatorError('*REQUIRED');
      isValid = false;
    } else {
      setCapaCreatorError('');
    }

    if (!capaTrigger || capaTrigger?.label === null) {
      setCapaTriggerError('*REQUIRED');
      isValid = false;
    } else {
      setCapaTriggerError('');
    }

    if (!severity || severity?.label === null) {
      setSeverityError('*REQUIRED');
      isValid = false;
    } else if (severity.label === 'Other' && !specifyOtherSeverity) {
      setSpecifySeverityError('*REQUIRED');
      isValid = false;
    } else {
      setSeverityError('');
      setSpecifySeverityError('');
    }

    if (!selectedResponsiblePerson || selectedResponsiblePerson?.label === null) {
      setResponsiblePersonError('*REQUIRED');
      isValid = false;
    } else {
      setResponsiblePersonError('');
    }

    if (!category || category?.label === null) {
      setCategoryError('*REQUIRED');
      isValid = false;
    } else if (category.label === 'Other' && !specifyOtherCategory) {
      setCategoryOtherError('*REQUIRED');
      isValid = false;
    } else if (category.label === 'Study' && selectedStudies.length < 1) {
      setCategoryStudyError('*REQUIRED');
      isValid = false;
    } else {
      setCategoryError('');
      setCategoryOtherError('');
      setCategoryStudyError('');
    }

    if (!targetDate) {
      setTargetDateError('*REQUIRED');
      isValid = false;
    } else if (targetDate < dateInitiated) {
      setTargetDateError('Target Date cannot be before Date Initiated.');
      isValid = false;
    } else {
      setTargetDateError('');
    }

    if (!status || status?.label === null) {
      setStatusError('*REQUIRED');
      isValid = false;
    } else {
      setStatusError('');
    }

    if (selectedEscalationUsers.length === 0) {
      setEscalatedUsersError('*REQUIRED');
      isValid = false;
    } else {
      setEscalatedUsersError('');
    }

    return isValid;
  };

  const handleSaveClick = () => {
    const validated = validateFields();

    if (!validated) {
      return;
    }

    if (editMode) {
      const newCapa = {
        ...capa,
        dateInitiated: formatDateAsString(dateInitiated),
        capaCreator: capaCreator?.value?.externalReference || capaCreator?.value,
        capaTrigger: capaTrigger?.value,
        capaSeverity: severity?.value,
        specifySeverity: specifyOtherSeverity,
        description: description,
        capaCategory: category?.value,
        responsiblePerson: selectedResponsiblePerson?.value,
        ccUsers: selectedCcUsers.map((user) => user.value?.personExternalReference || user.value?.externalReference),
        escalateToUsers:
          selectedEscalationUsers?.map(
            (user) => user?.value?.personExternalReference || user?.value?.externalReference
          ) || [],
        studies: selectedStudies?.map((study) => ({
          location: study.value?.locationExternalReference,
          study: study.value?.studyExternalReference,
        })),
        targetDate: targetDate,
        status: status?.value,
        specifyCategory: specifyOtherCategory,
        emailFrequencyHours: emailFrequencyHours,
        escalateAfterDays: escalateAfterDays,
      };
      handleUpdate(newCapa);
    } else {
      const capaToSave = {
        dateInitiated: formatDateAsString(dateInitiated),
        capaCreator: capaCreator?.value?.externalReference,
        capaTrigger: capaTrigger?.value,
        capaSeverity: severity?.value,
        specifySeverity: specifyOtherSeverity,
        description: description,
        capaCategory: category?.value,
        responsiblePerson: selectedResponsiblePerson?.value?.externalReference,
        ccUsers: selectedCcUsers.map((user) => user.value?.externalReference),
        escalateToUsers:
          selectedEscalationUsers?.map(
            (user) => user?.value?.personExternalReference || user?.value?.externalReference
          ) || [],
        studies: selectedStudies?.map((study) => ({
          location: study.value?.locationExternalReference,
          study: study.value?.studyExternalReference,
        })),
        targetDate: targetDate,
        status: status?.value,
        specifyCategory: specifyOtherCategory,
        emailFrequencyHours: emailFrequencyHours,
        escalateAfterDays: escalateAfterDays,
      };

      saveCAPA(capaToSave);
    }
  };

  const saveCAPA = async (capa) => {
    setSpinnerText('Creating CAPA');
    const [data, error] = await createCAPA(context.username, capa);
    if (data) {
      if (data.responseStatus === 'Success') {
        showToaster({
          responseStatus: 'Success',
          responseMessage: 'CAPA Created',
        });
        navigateTo('capaReviewsTable');
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const handleStudiesChange = (selectedOptions) => {
    setSelectedStudies(selectedOptions);
  };

  const handleRemoveStudy = (studyExternalRef) => {
    const filteredStudies = selectedStudies.filter((study) => study.value.studyExternalReference !== studyExternalRef);
    setSelectedStudies(filteredStudies);
  };

  const handleCcUsersChange = (selectedOptions) => {
    setSelectedCcUsers((prevSelectedUsers) => {
      const uniqueUsers = selectedOptions.filter(
        (user) => !prevSelectedUsers.some((prevUser) => prevUser.value === user.value)
      );
      return [...prevSelectedUsers, ...uniqueUsers];
    });
  };

  const handleRemoveCcUser = (index) => {
    setSelectedCcUsers((prevSelectedUsers) => {
      const updatedUsers = [...prevSelectedUsers];
      updatedUsers.splice(index, 1);
      return updatedUsers;
    });
  };

  const handleEscalatedUsersChange = (selectedOptions) => {
    setSelectedEscalationUsers((prevSelectedUsers) => {
      const prevUsersArray = Array.isArray(prevSelectedUsers) ? prevSelectedUsers : [];
      const uniqueUsers = selectedOptions.filter(
        (user) => !prevUsersArray.some((prevUser) => prevUser.value === user.value)
      );
      return [...prevUsersArray, ...uniqueUsers];
    });
  };

  const handleRemoveEscalatedUser = (index) => {
    setSelectedEscalationUsers((prevSelectedUsers) => {
      const updatedUsers = [...prevSelectedUsers];
      updatedUsers.splice(index, 1);
      return updatedUsers;
    });
  };

  const fieldsDisabled = !hasRoles(context, roles);
  const initiatedAndCreatorDisabled = fieldsDisabled || editMode;

  const renderLeftInputs = () => {
    return (
      <div className="form-section-left">
        <div className="input-container">
          <label className={'input-label'}>Date Initiated:</label>
          <DatePicker
            selected={dateInitiated}
            className={`text-input ${dateInitiatedError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={initiatedAndCreatorDisabled}
            onChange={(selectedDate) => setDateInitiated(selectedDate)}
            popperModifiers={DATE_PICKER_POPPER_MODIFIERS}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaDateInitiated123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Date Initiated:'}
            />
          )}
        </div>
        <div className="error-message">
          {dateInitiatedError && <div className="small-red-text">{dateInitiatedError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>CAPA Creator:</label>
          <Select
            options={responsiblePersonList}
            closeMenuOnSelect={true}
            onChange={setCapaCreator}
            value={capaCreator || null}
            isDisabled={initiatedAndCreatorDisabled}
            className="input-select"
            classNamePrefix={`input-select${capaCreatorError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaCreator123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'CAPA Creator:'}
            />
          )}
        </div>
        <div className="error-message">
          {capaCreatorError && <div className="small-red-text">{capaCreatorError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>CAPA Trigger:</label>
          <Select
            options={triggersList.current.map((trigger, index) => ({
              value: trigger,
              label: trigger,
            }))}
            closeMenuOnSelect={true}
            onChange={setCapaTrigger}
            value={capaTrigger || null}
            isDisabled={fieldsDisabled}
            className="input-select"
            classNamePrefix={`input-select${capaTriggerError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaTrigger23'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'CAPA Trigger:'}
            />
          )}
        </div>
        <div className="error-message">
          {capaTriggerError && <div className="small-red-text">{capaTriggerError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Severity:</label>
          <Select
            options={severityList.current.map((severity, index) => ({
              value: severity,
              label: severity,
            }))}
            closeMenuOnSelect={true}
            onChange={handleSeverityChange}
            value={severity || null}
            isDisabled={fieldsDisabled}
            className="input-select"
            classNamePrefix={`input-select${severityError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaSeverity123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Severity:'}
            />
          )}
        </div>
        <div className="error-message">{severityError && <div className="small-red-text">{severityError}</div>}</div>
        {showOtherSeverity && (
          <div>
            <div className="input-container">
              <label className={'input-area-label'}>Specify:</label>
              <textarea
                className={`input-area-sm ${specifySeverityError && 'error-border'}`}
                spellCheck={true}
                type="text"
                value={specifyOtherSeverity}
                disabled={fieldsDisabled}
                onChange={({ target }) => {
                  setSpecifyOtherSeverity(target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'capaSpecifyOtherSeverity123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Specify:'}
                />
              )}
            </div>
            <div className="error-message">
              {specifySeverityError && <div className="small-red-text">{specifySeverityError}</div>}
            </div>
          </div>
        )}
        <div className="input-container">
          <label className={'input-label'}>Responsible Person:</label>
          <Select
            options={responsiblePersonList}
            closeMenuOnSelect={true}
            onChange={setSelectedResponsiblePerson}
            value={selectedResponsiblePerson || null}
            isDisabled={fieldsDisabled}
            className="input-select"
            classNamePrefix={`input-select${responsiblePersonError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaResponsiblePerson123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Responsible Person:'}
            />
          )}
        </div>
        <div className="error-message">
          {responsiblePersonError && <div className="small-red-text">{responsiblePersonError}</div>}
        </div>
        <div className={'input-container'}>
          <label className="input-label">Email Frequency: </label>
          <label className="input-label-sm-capa">Every </label>
          <input
            className="text-input-xsmall"
            type="text"
            value={emailFrequencyHours}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setEmailFrequencyHours(target.value);
            }}
          />
          <label className="input-label-sm-capa"> hours, escalate after </label>
          <input
            className="text-input-xsmall"
            type="text"
            value={escalateAfterDays}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setEscalateAfterDays(target.value);
            }}
          />
          <label className="input-label-sm-capa"> days. </label>
        </div>
        <div className="input-container-main">
          <div className="input-container">
            <label className="input-label">CC Users:</label>
            <Select
              options={responsiblePersonList}
              closeMenuOnSelect={true}
              isMulti={true}
              onChange={handleCcUsersChange}
              className="input-select"
              classNamePrefix="input-select"
              controlShouldRenderValue={false}
              isDisabled={fieldsDisabled}
              value={selectedCcUsers}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'capaCCUsers123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'CC Users:'}
              />
            )}
          </div>

          {selectedCcUsers && selectedCcUsers.length > 0 && (
            <div
              className="capa-review-cc-users-container"
              style={{ overflowY: selectedCcUsers?.length > 3 ? 'auto' : 'hidden' }}
            >
              {selectedCcUsers.map((user, index) => (
                <div key={index} className="capa-review-cc-user">
                  <input
                    type="text"
                    value={`${user.label}`}
                    readOnly
                    disabled={fieldsDisabled}
                    className="text-input"
                  />
                  <button
                    className="delete-section-button"
                    type="button"
                    disabled={fieldsDisabled}
                    onClick={() => handleRemoveCcUser(index)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="input-container-main">
          <div className="input-container">
            <label className="input-label">Escalation Contact:</label>
            <Select
              options={responsiblePersonList}
              closeMenuOnSelect={true}
              isMulti={true}
              onChange={handleEscalatedUsersChange}
              className="input-select"
              classNamePrefix={`input-select${escalatedUsersError && 'error-border'}`}
              controlShouldRenderValue={false}
              isDisabled={fieldsDisabled}
              value={selectedEscalationUsers}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'capaEscalationContact123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Escalation Contact:'}
              />
            )}
          </div>
          <div className="error-message">
            {escalatedUsersError && <div className="small-red-text">{escalatedUsersError}</div>}
          </div>

          {selectedEscalationUsers && selectedEscalationUsers.length > 0 && (
            <div
              className="capa-review-cc-users-container"
              style={{ overflowY: selectedEscalationUsers?.length > 3 ? 'auto' : 'hidden' }}
            >
              {selectedEscalationUsers.map((user, index) => (
                <div key={index} className="capa-review-cc-user">
                  <input
                    type="text"
                    value={`${user.label}`}
                    readOnly
                    disabled={fieldsDisabled}
                    className="text-input"
                  />
                  <button
                    className="delete-section-button"
                    type="button"
                    disabled={fieldsDisabled}
                    onClick={() => handleRemoveEscalatedUser(index)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRightInputs = () => {
    return (
      <div className="form-section-right">
        <div className="input-container">
          <label className={'input-label-right'}>Category:</label>
          <Select
            options={categoryList.current.map((category, index) => ({
              value: category,
              label: category,
            }))}
            closeMenuOnSelect={true}
            onChange={handleCategoryChange}
            value={category || null}
            isDisabled={fieldsDisabled}
            className="input-select"
            classNamePrefix={`input-select${categoryError && 'error-border'}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaCategory123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Category:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {categoryError && <div className="small-red-text">{categoryError}</div>}
        </div>
        {showOtherCategory && (
          <div>
            <div className="input-container">
              <label className={'input-area-label-right'}>Specify:</label>
              <textarea
                className={`input-area-sm ${categoryOtherError && 'error-border'}`}
                spellCheck={true}
                type="text"
                value={specifyOtherCategory}
                disabled={fieldsDisabled}
                onChange={({ target }) => {
                  setSpecifyOtherCategory(target.value);
                }}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'capaSpecifyOtherCategory123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Specify:'}
                />
              )}
            </div>
            <div className="error-message-right">
              {categoryOtherError && <div className="small-red-text">{categoryOtherError}</div>}
            </div>
          </div>
        )}
        {showStudies && (
          <div className="capa-review-study-container">
            <div className="capa-review-study-dropdown-container">
              <label className="input-label-right">Studies:</label>
              <Select
                options={studiesList}
                closeMenuOnSelect={true}
                isMulti={true}
                onChange={handleStudiesChange}
                className="input-select"
                classNamePrefix={`input-select${categoryStudyError && 'error-border'}`}
                controlShouldRenderValue={false}
                isDisabled={fieldsDisabled}
                value={selectedStudies}
              />
              {toolTipsEnabled && (
                <ToolTip
                  tooltipExternalReference={'capaStudies123'}
                  context={context}
                  setSpinnerText={setSpinnerText}
                  showToaster={showToaster}
                  fieldName={'Studies:'}
                />
              )}
            </div>
            <div className="error-message-right">
              {categoryStudyError && <div className="small-red-text">{categoryStudyError}</div>}
            </div>

            {selectedStudies && selectedStudies.length > 0 && (
              <div
                className="capa-review-selected-studies"
                style={{ overflowY: selectedStudies?.length > 3 ? 'auto' : 'hidden' }}
              >
                {selectedStudies.map((study, index) => (
                  <div key={index} className="capa-review-selected-study">
                    <input
                      type="text"
                      value={`${study.label}`}
                      readOnly
                      disabled={fieldsDisabled}
                      className="text-input"
                    />
                    <button
                      className="delete-section-button"
                      type="button"
                      disabled={fieldsDisabled}
                      onClick={() => handleRemoveStudy(study.value.studyExternalReference)}
                    >
                      X
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="input-container">
          <label className={'input-area-label-right'}>Description:</label>
          <textarea
            className="input-area-sm"
            spellCheck={true}
            type="text"
            value={description}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setDescription(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaDescription123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Description:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label-right'}>Target Date:</label>
          <DatePicker
            selected={targetDate}
            className={`text-input ${targetDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={fieldsDisabled}
            onChange={(selectedDate) => setTargetDate(selectedDate)}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaTargetDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Target Date:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {targetDateError && <div className="small-red-text">{targetDateError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label-right'}>Status:</label>
          <Select
            options={statusList.current.map((owner, index) => ({
              value: owner,
              label: owner,
            }))}
            closeMenuOnSelect={true}
            onChange={setStatus}
            value={status || null}
            isDisabled={fieldsDisabled}
            className="input-select"
            classNamePrefix={`input-select${statusError && 'error-border'}`}
            menuPlacement="top"
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'capaStatus123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Status:'}
            />
          )}
        </div>
        <div className="error-message-right">{statusError && <div className="small-red-text">{statusError}</div>}</div>
      </div>
    );
  };

  return (
    <div className="tab-container capa-review-general-tab">
      <div className="capa-review-input-container">
        {renderLeftInputs()}
        {renderRightInputs()}
      </div>
      <div className={'review-button-container'}>
        {editMode && (
          <>
            <SaveButton
              textColor={'#077187'}
              border={'1px solid #077187'}
              title={'Cancel'}
              onClick={() => navigateTo('capaReviewsTable')}
            />
            <SaveButton color="#077187" title="Save" disabled={fieldsDisabled} onClick={handleSaveClick} />
          </>
        )}
        {!editMode && (
          <>
            <SaveButton
              textColor={'#077187'}
              border={'1px solid #077187'}
              title={'Cancel'}
              onClick={() => navigateTo('capaReviewsTable')}
            />
            <SaveButton color={'#077187'} title={'Save'} onClick={handleSaveClick} />
          </>
        )}
      </div>
    </div>
  );
};
